import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface ApplicationRow {
  email: string;
  name: string;
  firmName: string | null;
  phone: string | null;
  createdAt: any;
}

interface ActiveCoachRow {
  email: string;
  name: string;
  firmName: string | null;
  phone: string | null;
  caseload: number;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export function CoachApplicationsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'pending' | 'active'>('pending');

  // ── Pending tab state ──
  const [rows, setRows] = useState<ApplicationRow[]>([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [errorPending, setErrorPending] = useState('');
  const [actingEmail, setActingEmail] = useState<string | null>(null);

  // ── Active tab state ──
  const [activeCoaches, setActiveCoaches] = useState<ActiveCoachRow[]>([]);
  const [loadingActive, setLoadingActive] = useState(false);
  const [errorActive, setErrorActive] = useState('');
  const [activeLoaded, setActiveLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadPending();
  }, [user]);

  useEffect(() => {
    if (tab === 'active' && !activeLoaded && user) loadActive();
  }, [tab, activeLoaded, user]);

  const loadPending = async () => {
    setLoadingPending(true);
    setErrorPending('');
    try {
      const snap = await getDocs(query(collection(db, 'coaches'), where('status', '==', 'pending_approval')));
      const loaded: ApplicationRow[] = snap.docs.map(d => {
        const c = d.data();
        return { email: d.id, name: c.name ?? '', firmName: c.firmName ?? null, phone: c.phone ?? null, createdAt: c.createdAt };
      });
      loaded.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
      setRows(loaded);
    } catch (e: any) {
      setErrorPending(e.message || 'Failed to load applications.');
    } finally {
      setLoadingPending(false);
    }
  };

  const loadActive = async () => {
    setLoadingActive(true);
    setErrorActive('');
    try {
      const coachSnap = await getDocs(query(collection(db, 'coaches'), where('status', '==', 'active')));
      const independent = coachSnap.docs
        .filter(d => d.data().coachType === 'independent')
        .map(d => ({ email: d.id, ...d.data() } as any));

      // Batch coachId `in` queries — Firestore limit is 30 per query.
      const caseloadMap = new Map<string, number>();
      if (independent.length > 0) {
        const emails = independent.map((c: any) => c.email);
        const chunks = chunk(emails, 30);
        await Promise.all(
          chunks.map(async (ch) => {
            const snap = await getDocs(query(collection(db, 'profiles'), where('coachId', 'in', ch)));
            snap.docs.forEach(d => {
              const id = d.data().coachId as string;
              caseloadMap.set(id, (caseloadMap.get(id) ?? 0) + 1);
            });
          })
        );
      }

      const loaded: ActiveCoachRow[] = independent.map((c: any) => ({
        email: c.email,
        name: c.name ?? '',
        firmName: c.firmName ?? null,
        phone: c.phone ?? null,
        caseload: caseloadMap.get(c.email) ?? 0,
      }));
      loaded.sort((a, b) => a.name.localeCompare(b.name));
      setActiveCoaches(loaded);
      setActiveLoaded(true);
    } catch (e: any) {
      setErrorActive(e.message || 'Failed to load active coaches.');
    } finally {
      setLoadingActive(false);
    }
  };

  const handleAction = async (email: string, action: 'approve' | 'reject') => {
    setActingEmail(email);
    try {
      const fn = action === 'approve' ? 'approveCoachSignup' : 'rejectCoachSignup';
      await httpsCallable(getFunctions(), fn)({ coachEmail: email });
      setRows(prev => prev.filter(r => r.email !== email));
    } catch (e: any) {
      setErrorPending(e.message || 'Action failed. Please try again.');
    } finally {
      setActingEmail(null);
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">Independent Coaches</h1>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
        {(['pending', 'active'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 20px',
              fontSize: 14,
              fontWeight: tab === t ? 600 : 400,
              background: 'none',
              border: 'none',
              borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === t ? 'var(--accent)' : 'var(--text-secondary)',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t === 'pending' ? 'Pending' : 'Active'}
          </button>
        ))}
      </div>

      {tab === 'pending' && (
        <>
          {errorPending && <div className="error-message" style={{ marginBottom: 16 }}>{errorPending}</div>}
          <div className="card" style={{ padding: 0 }}>
            {loadingPending ? (
              <div className="loading">Loading…</div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
                No pending applications.
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Firm</th>
                      <th>Phone</th>
                      <th>Applied</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.email}>
                        <td style={{ fontWeight: 500 }}>{r.name}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{r.email}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{r.firmName ?? '—'}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{r.phone ?? '—'}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{fmtDate(r.createdAt)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="btn-primary"
                              disabled={actingEmail === r.email}
                              onClick={() => handleAction(r.email, 'approve')}
                              style={{ padding: '5px 14px', fontSize: 13 }}
                            >
                              {actingEmail === r.email ? '…' : 'Approve'}
                            </button>
                            <button
                              className="btn-danger"
                              disabled={actingEmail === r.email}
                              onClick={() => handleAction(r.email, 'reject')}
                              style={{ padding: '5px 14px', fontSize: 13 }}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'active' && (
        <>
          {errorActive && <div className="error-message" style={{ marginBottom: 16 }}>{errorActive}</div>}
          <div className="card" style={{ padding: 0 }}>
            {loadingActive ? (
              <div className="loading">Loading…</div>
            ) : activeCoaches.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
                No active independent coaches.
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Firm</th>
                      <th>Caseload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCoaches.map(c => (
                      <tr key={c.email}>
                        <td style={{ fontWeight: 500 }}>{c.name}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{c.email}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{c.phone ?? '—'}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.firmName ?? '—'}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.caseload}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}
